import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import type { ArenaUiState, RoomBackend } from './types.ts';

interface ArenaContextValue {
  state: ArenaUiState;
  actions: RoomBackend;
}

const ArenaContext = createContext<ArenaContextValue | null>(null);

export function ArenaProvider({
  backend,
  children,
}: {
  backend: RoomBackend;
  children: ReactNode;
}) {
  const subscribe = useCallback((listener: () => void) => backend.subscribe(listener), [backend]);
  const getSnapshot = useCallback(() => backend.getState(), [backend]);
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const value = useMemo(() => ({ state, actions: backend }), [state, backend]);
  return <ArenaContext.Provider value={value}>{children}</ArenaContext.Provider>;
}

export function useArena(): ArenaContextValue {
  const value = useContext(ArenaContext);
  if (!value) throw new Error('useArena 必须在 ArenaProvider 内使用');
  return value;
}

export function useArenaState(): ArenaUiState {
  return useArena().state;
}

export function useArenaActions(): RoomBackend {
  return useArena().actions;
}
